import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GtmService {
  private isInitialized = false;
  private gtmId: string;

  constructor() {
    this.gtmId = environment.gtmId;
  }

  /**
   * Inicializa Google Tag Manager dinámicamente
   */
  initializeGTM(): void {
    if (this.isInitialized || !this.gtmId) {
      return;
    }

    // Crear el dataLayer si no existe
    window.dataLayer = window.dataLayer || [];

    // Crear la función gtag
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    // Inicializar GTM
    window.gtag('js', new Date());
    window.gtag('config', this.gtmId, {
      page_title: document.title,
      page_location: window.location.href,
    });

    // Cargar el script de GTM
    this.loadGTMScript();

    this.isInitialized = true;
    console.log(`GTM inicializado con ID: ${this.gtmId}`);
  }

  /**
   * Carga dinámicamente el script de GTM
   */
  private loadGTMScript(): void {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gtmId}`;
    document.head.appendChild(script);
  }

  /**
   * Envía un evento personalizado a GTM
   * @param eventName Nombre del evento
   * @param parameters Parámetros del evento
   */
  sendEvent(eventName: string, parameters?: any): void {
    if (!this.isInitialized) {
      console.warn('GTM no está inicializado. Llamando initializeGTM() primero.');
      this.initializeGTM();
    }

    window.gtag('event', eventName, parameters);
    console.log(`Evento GTM enviado: ${eventName}`, parameters);
  }

  /**
   * Envía un evento de página vista
   * @param pageTitle Título de la página
   * @param pageLocation URL de la página
   */
  sendPageView(pageTitle: string, pageLocation: string): void {
    this.sendEvent('page_view', {
      page_title: pageTitle,
      page_location: pageLocation,
    });
  }

  /**
   * Envía un evento de compra/transacción
   * @param transactionData Datos de la transacción
   */
  sendPurchaseEvent(transactionData: { transaction_id: string; value: number; currency: string; items?: any[] }): void {
    this.sendEvent('purchase', {
      transaction_id: transactionData.transaction_id,
      value: transactionData.value,
      currency: transactionData.currency,
      items: transactionData.items || [],
    });
  }

  /**
   * Envía un evento de inicio de proceso de pago
   * @param checkoutData Datos del checkout
   */
  sendBeginCheckoutEvent(checkoutData: { currency: string; value: number; items?: any[] }): void {
    this.sendEvent('begin_checkout', {
      currency: checkoutData.currency,
      value: checkoutData.value,
      items: checkoutData.items || [],
    });
  }

  /**
   * Envía un evento de registro/fidelización
   * @param method Método de registro (email, card, etc.)
   */
  sendSignUpEvent(method: string): void {
    this.sendEvent('sign_up', {
      method: method,
    });
  }

  /**
   * Verifica si GTM está inicializado
   */
  isGTMInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Obtiene el ID de GTM actual
   */
  getGTMId(): string {
    return this.gtmId;
  }

  /**
   * Obtiene información detallada del estado de GTM para diagnóstico
   */
  getGTMDiagnosticInfo(): any {
    const environment = this.getEnvironmentName();
    const gtmScriptExists = this.checkGTMScriptExists();
    const dataLayerExists = typeof window !== 'undefined' && window.dataLayer !== undefined;
    const gtagFunctionExists = typeof window !== 'undefined' && typeof window.gtag === 'function';

    return {
      environment: environment,
      gtmId: this.gtmId,
      isInitialized: this.isInitialized,
      gtmScriptExists: gtmScriptExists,
      dataLayerExists: dataLayerExists,
      gtagFunctionExists: gtagFunctionExists,
      dataLayerLength: dataLayerExists ? window.dataLayer.length : 0,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'N/A',
      url: typeof window !== 'undefined' ? window.location.href : 'N/A',
    };
  }

  /**
   * Verifica si el script de GTM está cargado en el DOM
   */
  private checkGTMScriptExists(): boolean {
    if (typeof document === 'undefined') return false;
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src.includes('googletagmanager.com/gtag/js')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Obtiene el nombre del ambiente actual
   */
  private getEnvironmentName(): string {
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      if (hostname.includes('localhost')) return 'Local Development';
      if (hostname.includes('dev.')) return 'Development';
      if (hostname.includes('alpha.')) return 'Alpha/Staging';
      if (hostname.includes('happyland.cl')) return 'Production';
    }
    return 'Unknown';
  }

  /**
   * Ejecuta una verificación completa de GTM y muestra los resultados en consola
   */
  runDiagnostics(): void {
    const diagnostics = this.getGTMDiagnosticInfo();

    console.group('🔍 GTM Diagnostic Report');
    console.log('📍 Environment:', diagnostics.environment);
    console.log('🏷️  GTM ID:', diagnostics.gtmId);
    console.log('✅ Is Initialized:', diagnostics.isInitialized);
    console.log('📄 GTM Script Exists:', diagnostics.gtmScriptExists);
    console.log('📊 DataLayer Exists:', diagnostics.dataLayerExists);
    console.log('🔧 Gtag Function Exists:', diagnostics.gtagFunctionExists);
    console.log('📈 DataLayer Length:', diagnostics.dataLayerLength);
    console.log('🕐 Timestamp:', diagnostics.timestamp);
    console.log('🌐 URL:', diagnostics.url);

    if (diagnostics.dataLayerExists && window.dataLayer.length > 0) {
      console.log('📋 DataLayer Contents:', window.dataLayer);
    }

    console.groupEnd();

    // Enviar evento de diagnóstico
    if (this.isInitialized) {
      this.sendEvent('gtm_diagnostic_check', {
        environment: diagnostics.environment,
        gtm_id: diagnostics.gtmId,
        script_loaded: diagnostics.gtmScriptExists,
        data_layer_items: diagnostics.dataLayerLength,
      });
    }
  }

  /**
   * Envía un evento de prueba para verificar que GTM está funcionando
   */
  sendTestEvent(): void {
    const testData = {
      test_category: 'GTM_Test',
      test_label: 'Configuration_Check',
      test_value: 1,
      environment: this.getEnvironmentName(),
      timestamp: new Date().toISOString(),
    };

    this.sendEvent('gtm_test_event', testData);
    console.log('🧪 Evento de prueba GTM enviado:', testData);
  }
}
